import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { BookOpen, ArrowLeftRight, Calendar, Landmark, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export default function MoneyLedger() {
  const [groupBy, setGroupBy] = useState("month");
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);   // { row, entries }
  const { display, other, rate, toggle, fmt } = useCurrency({ base: "SBD", other: "AUD" });

  useEffect(() => {
    api.get(`/money/journals/report?group_by=${groupBy}`).then((r) => setData(r.data));
  }, [groupBy]);

  const openDrill = async (row) => {
    let entries = [];
    if (groupBy === "month") entries = (await api.get(`/money/journals/entries?month=${row.label}&limit=50`)).data;
    else entries = (await api.get(`/money/journals/entries?limit=50`)).data.filter((e) => e.lines.some(() => true));
    setDrill({ row, entries });
  };

  if (!data) return <div className="text-sm text-surface-onVariant">Loading ledger…</div>;

  return (
    <div className="animate-fade-in-up" data-testid="ledger-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-medium text-surface-on tracking-tight">General Ledger</h3>
          <p className="text-xs text-surface-onVariant">Auto-posted journal entries tie every matched settlement back to your books</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="ledger-currency"
            className="inline-flex items-center gap-1 rounded-full bg-surface-container hover:bg-primary/8 text-surface-on text-xs font-medium px-3 py-1.5 transition-colors border border-outline-variant"
            title={rate ? `1 SBD = ${rate.toFixed(4)} AUD` : ""}>
            <ArrowLeftRight className="w-3.5 h-3.5" /> View in {other}
          </button>
          <div className="inline-flex items-center gap-1 rounded-full bg-surface-container p-1">
            <button onClick={() => setGroupBy("month")} data-testid="group-month"
              className={cn("px-3 py-1.5 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-1.5",
                groupBy === "month" ? "bg-primary text-primary-foreground" : "text-surface-on hover:bg-primary/8")}>
              <Calendar className="w-3.5 h-3.5" /> By month
            </button>
            <button onClick={() => setGroupBy("account")} data-testid="group-account"
              className={cn("px-3 py-1.5 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-1.5",
                groupBy === "account" ? "bg-primary text-primary-foreground" : "text-surface-on hover:bg-primary/8")}>
              <Landmark className="w-3.5 h-3.5" /> By GL account
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <StatCard label="Journal entries" value={data.row_count} />
        <StatCard label="Total debit" value={fmt(data.total_debit_cents)} tone="primary" />
        <StatCard label="Total credit" value={fmt(data.total_credit_cents)} tone={data.total_debit_cents === data.total_credit_cents ? "positive" : "danger"} />
      </div>

      {data.rows.length === 0 ? (
        <div className="rounded-hero bg-surface-container p-10 text-center">
          <BookOpen className="w-8 h-8 mx-auto text-surface-onVariant" />
          <p className="text-sm text-surface-onVariant mt-3">No journal entries yet. Run reconciliation to auto-post entries.</p>
        </div>
      ) : (
        <div className="bg-surface-container rounded-hero overflow-hidden" data-testid="ledger-table">
          <table className="w-full text-sm">
            <thead className="bg-surface-containerHigh text-[10px] uppercase tracking-widest text-surface-onVariant">
              <tr>
                <th className="text-left px-4 py-2">{groupBy === "month" ? "Period" : "GL Account / Category"}</th>
                <th className="text-right px-4 py-2">Entries</th>
                <th className="text-right px-4 py-2">Debit</th>
                <th className="text-right px-4 py-2">Credit</th>
                <th className="text-right px-4 py-2">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => {
                const bal = row.debit_cents - row.credit_cents;
                return (
                  <tr key={row.label} data-testid={`ledger-row-${row.label}`}
                    onClick={() => openDrill(row)}
                    className="border-t border-outline-variant/40 cursor-pointer hover:bg-primary/5 transition-colors">
                    <td className="px-4 py-3 text-surface-on font-medium">{row.label}</td>
                    <td className="px-4 py-3 text-right text-surface-on tabular-nums">{row.entry_count}</td>
                    <td className="px-4 py-3 text-right text-surface-on tabular-nums">{fmt(row.debit_cents)}</td>
                    <td className="px-4 py-3 text-right text-surface-on tabular-nums">{fmt(row.credit_cents)}</td>
                    <td className={cn("px-4 py-3 text-right tabular-nums font-medium", bal === 0 ? "text-[#0F5027]" : bal > 0 ? "text-primary" : "text-danger")}>
                      {bal === 0 ? "—" : (bal > 0 ? "Dr " : "Cr ") + fmt(Math.abs(bal))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <JournalDrillDialog drill={drill} onClose={() => setDrill(null)} fmt={fmt} groupBy={groupBy} />
    </div>
  );
}

function JournalDrillDialog({ drill, onClose, fmt, groupBy }) {
  if (!drill) return null;
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] bg-surface rounded-hero border-none p-6 flex flex-col" data-testid="journal-drill-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">
          Journal entries · {drill.row.label}
        </DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-surface-onVariant">
          {drill.entries.length} entries · {fmt(drill.row.debit_cents)} Dr / {fmt(drill.row.credit_cents)} Cr
        </DialogDescription>
        <div className="flex-1 overflow-y-auto mt-4 space-y-3">
          {drill.entries.map((j) => (
            <div key={j.id} className="rounded-2xl bg-surface-container p-4">
              <div className="flex items-center justify-between text-xs text-surface-onVariant mb-2">
                <span className="font-mono">{j.date}</span>
                <span className="truncate ml-2">{j.memo}</span>
              </div>
              <div className="space-y-1">
                {j.lines.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 font-mono text-xs">
                    <span className={cn("px-2 py-0.5 rounded shrink-0 w-14 text-center", l.side === "debit" ? "bg-surface-containerHigh text-surface-on" : "bg-primary-container text-primary-onContainer")}>
                      {l.side.toUpperCase()}
                    </span>
                    <span className="flex-1 truncate text-surface-onVariant">{l.memo}</span>
                    <span className="tabular-nums text-surface-on">{fmt(l.amount_cents)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, tone = "" }) {
  const cls = tone === "primary" ? "text-primary" : tone === "positive" ? "text-[#0F5027]" : tone === "danger" ? "text-danger" : "text-surface-on";
  return (
    <div className="bg-surface-container rounded-2xl px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-surface-onVariant">{label}</div>
      <div className={cn("text-lg font-medium tabular-nums mt-1", cls)}>{value}</div>
    </div>
  );
}
